import { DatePickerMultiple } from '@/global/components/DatePickerMultiple'
import { DatePickerSingle } from '@/global/components/DatePickerSingle'
import { SelectEmpresa, SelectFuncionario } from '@/global/components/Selects'
import { cn } from '@/lib/utils'
import {
    Alert,
    Box,
    Card,
    CardContent,
    Chip,
    FormLabel,
    Grid,
    Typography,
} from '@mui/material'
import dayjs from 'dayjs'
import { XIcon } from 'lucide-react'
import { Controller, useFormContext } from 'react-hook-form'

export default function FormCompensacaoManual() {
    const { control, watch, getValues, setValue } = useFormContext()

    const tipo = watch('tipo')
    const data = watch('data')
    const destinos = watch('destinos') || []
    const empresaId = getValues('empresaId')
    const funcionarioId = getValues('funcionarioId')

    const isEditMode = Boolean(empresaId && funcionarioId)

    const getAlertMessage = () => {
        if (!tipo) {
            return 'Selecione o tipo de compensação para continuar'
        }

        const dataFormatada = data
            ? dayjs(data).format('DD/MM/YYYY (dddd)')
            : ''

        if (tipo === 'COMPENSAR') {
            if (destinos.length === 0) {
                return `O dia ${dataFormatada} possui horas extras. Você pode adicionar os dias com faltas que deseja compensar.`
            }
            return `O dia ${dataFormatada} possui horas extras que vão compensar as faltas de ${destinos.length} dia(s) selecionado(s).`
        }

        if (tipo === 'COMPENSADO') {
            if (destinos.length === 0) {
                return `O dia ${dataFormatada} possui faltas. Você pode adicionar os dias com horas extras para compensar.`
            }
            return `As faltas do dia ${dataFormatada} serão compensadas pelas horas extras de ${destinos.length} dia(s) selecionado(s).`
        }

        return null
    }

    const getDataLabel = () => {
        if (tipo === 'COMPENSAR') return 'Dia com horas extras'
        if (tipo === 'COMPENSADO') return 'Dia com faltas'
        return 'Data'
    }

    return (
        <Grid container spacing={3}>
            <Grid item xs={12}>
                <SelectEmpresa
                    width="100%"
                    size="medium"
                    disabled={isEditMode}
                />
            </Grid>
            <Grid item xs={12}>
                <SelectFuncionario
                    width="100%"
                    size="medium"
                    disabled={isEditMode}
                />
            </Grid>

            <Grid item xs={12}>
                <FormLabel component="legend" sx={{ mb: 2 }}>
                    Tipo de compensação
                </FormLabel>
                <Controller
                    name="tipo"
                    control={control}
                    render={({ field }) => (
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <Card
                                className={cn(
                                    'flex-1 cursor-pointer transition-all',
                                    'hover:shadow-lg',
                                    {
                                        'ring-2 ring-primary shadow-lg':
                                            field.value === 'COMPENSAR',
                                    },
                                )}
                                onClick={() => field.onChange('COMPENSAR')}
                            >
                                <CardContent>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1.5,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: 20,
                                                height: 20,
                                                borderRadius: '50%',
                                                border: 2,
                                                borderColor:
                                                    field.value === 'COMPENSAR'
                                                        ? 'primary.main'
                                                        : 'grey.400',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            {field.value === 'COMPENSAR' && (
                                                <Box
                                                    sx={{
                                                        width: 12,
                                                        height: 12,
                                                        borderRadius: '50%',
                                                        backgroundColor:
                                                            'primary.main',
                                                    }}
                                                />
                                            )}
                                        </Box>
                                        <Typography
                                            variant="h6"
                                            component="div"
                                        >
                                            Compensar
                                        </Typography>
                                    </Box>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ mt: 1, ml: 4.5 }}
                                    >
                                        Dia com horas extras
                                    </Typography>
                                </CardContent>
                            </Card>

                            <Card
                                className={cn(
                                    'flex-1 cursor-pointer transition-all',
                                    'hover:shadow-lg',
                                    {
                                        'ring-2 ring-primary shadow-lg':
                                            field.value === 'COMPENSADO',
                                    },
                                )}
                                onClick={() => field.onChange('COMPENSADO')}
                            >
                                <CardContent>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1.5,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: 20,
                                                height: 20,
                                                borderRadius: '50%',
                                                border: 2,
                                                borderColor:
                                                    field.value === 'COMPENSADO'
                                                        ? 'primary.main'
                                                        : 'grey.400',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            {field.value === 'COMPENSADO' && (
                                                <Box
                                                    sx={{
                                                        width: 12,
                                                        height: 12,
                                                        borderRadius: '50%',
                                                        backgroundColor:
                                                            'primary.main',
                                                    }}
                                                />
                                            )}
                                        </Box>
                                        <Typography
                                            variant="h6"
                                            component="div"
                                        >
                                            Compensado
                                        </Typography>
                                    </Box>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ mt: 1, ml: 4.5 }}
                                    >
                                        Dia com faltas
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Box>
                    )}
                />
            </Grid>

            <Grid item xs={12} md={6}>
                <Controller
                    name="data"
                    control={control}
                    render={({ field }) => (
                        <DatePickerSingle
                            {...field}
                            label={getDataLabel()}
                            disabled={!tipo}
                        />
                    )}
                />
            </Grid>

            <Grid item xs={12} md={6}>
                <Controller
                    name="destinos"
                    control={control}
                    render={({ field }) => (
                        <DatePickerMultiple
                            {...field}
                            label={
                                tipo === 'COMPENSAR'
                                    ? 'Dias com faltas a compensar'
                                    : tipo === 'COMPENSADO'
                                      ? 'Dias com horas extras'
                                      : 'Dias relacionados'
                            }
                            disabled={!tipo}
                        />
                    )}
                />
            </Grid>

            {destinos.length > 0 && (
                <Grid item xs={12}>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {destinos
                            .sort(
                                (a: { data: Date }, b: { data: Date }) =>
                                    new Date(a.data).getTime() -
                                    new Date(b.data).getTime(),
                            )
                            .map((item: { data: Date }, index: number) => (
                                <Chip
                                    key={index}
                                    label={dayjs
                                        .utc(item.data)
                                        .format('DD/MM/YYYY')}
                                    onDelete={() => {
                                        const newDestinos = destinos.filter(
                                            (_: any, i: number) => i !== index,
                                        )
                                        setValue('destinos', newDestinos)
                                    }}
                                    deleteIcon={<XIcon className="size-4" />}
                                    color="primary"
                                    variant="outlined"
                                />
                            ))}
                    </Box>
                </Grid>
            )}

            <Grid item xs={12}>
                <Alert severity={!tipo ? 'warning' : 'info'}>
                    {getAlertMessage()}
                </Alert>
            </Grid>
        </Grid>
    )
}
